import React, { useEffect, useRef, useState } from 'react';
import { assetTracker } from './assetTracker';
import { fetchAssetBlob } from '../../../utils/assetOpener';

/**
 * Phase 92D.2 — authenticated image loader.
 * Report/profile images are served behind auth (Authorization header), so a
 * plain <img> cannot load them. AuthImg fetches the bytes via fetchAssetBlob
 * (Bearer token from localStorage, allowlisted paths only) and renders a blob
 * object URL. It distinguishes:
 *   - no src            → render `fallback` (athlete genuinely has no photo)
 *   - src but load fail → render `fallback` AND record the broken ref (so we can
 *     surface/fix the underlying asset), never a blank box.
 * Registers with assetTracker so the PDF render waits for real settling.
 */
const AuthImg = ({ src, alt, className, fallback = null, testId }) => {
  const [url, setUrl] = useState(null);
  const [failed, setFailed] = useState(false);
  const registered = useRef(false);

  useEffect(() => {
    let revoked = null;
    let cancelled = false;
    setUrl(null); setFailed(false);
    if (!src) return undefined;
    registered.current = true;
    assetTracker.register();
    const settle = (broken) => { if (registered.current) { registered.current = false; assetTracker.settle(broken); } };
    (async () => {
      try {
        // Same-origin backend-served asset (starts with /api) → fetch with auth.
        const res = await fetchAssetBlob(src, alt);
        if (cancelled) { settle(); return; }
        const obj = URL.createObjectURL(res.blob);
        revoked = obj; setUrl(obj); settle();
      } catch (e) {
        if (cancelled) { settle(); return; }
        // eslint-disable-next-line no-console
        console.warn('[AuthImg] broken asset reference:', src, e?.status);
        setFailed(true); settle(src);
      }
    })();
    return () => { cancelled = true; if (revoked) URL.revokeObjectURL(revoked); };
  }, [src, alt]);

  if (!src || failed) return fallback;
  if (!url) return fallback; // brief placeholder while fetching
  return <img src={url} alt={alt || ''} className={className} data-testid={testId} />;
};

export default AuthImg;
